import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import LogisticsQuoteSectionIcon from "../../components/logistics/LogisticsQuoteSectionIcon";
import "../../assets/styles/logistics_quote_modal.scss";

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatExpoDate(v) {
  if (v == null || String(v).trim() === "") return "—";
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    try {
      const d = new Date(`${s}T12:00:00`);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
    } catch {
      /* ignore */
    }
  }
  try {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  } catch {
    /* ignore */
  }
  return s;
}

function mapCountriesResponse(response) {
  const apiCountries = Array.isArray(response?.data)
    ? response.data
    : response?.data?.countries || response?.data?.master_countries || [];
  return apiCountries
    .map((country) => {
      const id = country.country_id ?? country.id ?? country.value;
      const label =
        country.country_name ?? country.country_label ?? country.label ?? country.name ?? country.country;
      return { id: id != null ? String(id) : "", label: label != null ? String(label) : "" };
    })
    .filter((c) => c.id && c.label);
}

function mapStatesResponse(response) {
  const apiStates = Array.isArray(response?.data) ? response.data : response?.data?.states || response?.data?.master_states || [];
  return apiStates
    .map((state) => {
      const id = state.state_id ?? state.id ?? state.value;
      const label = state.state_name ?? state.state_label ?? state.label ?? state.name ?? state.state;
      return { id: id != null ? String(id) : "", label: label != null ? String(label) : "" };
    })
    .filter((s) => s.id && s.label);
}

function mapCitiesResponse(response) {
  const apiCities = Array.isArray(response?.data) ? response.data : response?.data?.cities || response?.data?.master_cities || [];
  return apiCities
    .map((city) => {
      const id = city.city_id ?? city.id ?? city.value;
      const label = city.city_name ?? city.city_label ?? city.label ?? city.name ?? city.city;
      return { id: id != null ? String(id) : "", label: label != null ? String(label) : "" };
    })
    .filter((c) => c.id && c.label);
}

async function resolveLocationLabels(countryId, stateId, cityId) {
  const cid = countryId != null && String(countryId).trim() !== "" ? String(countryId).trim() : "";
  const sid = stateId != null && String(stateId).trim() !== "" ? String(stateId).trim() : "";
  const cyid = cityId != null && String(cityId).trim() !== "" ? String(cityId).trim() : "";
  if (!cid) return { country: "—", state: "—", city: "—" };
  try {
    const cRes = await CommonService.get("/master/company-Country");
    const countries = mapCountriesResponse(cRes);
    const countryLabel = countries.find((c) => c.id === cid)?.label || cid;

    let stateLabel = "—";
    if (sid) {
      const sRes = await CommonService.get(`/master/company-State/${cid}`);
      const states = mapStatesResponse(sRes);
      stateLabel = states.find((s) => s.id === sid)?.label || sid;
    }

    let cityLabel = "—";
    if (cyid && sid) {
      const cityRes = await CommonService.get(`/master/company-City/${sid}`);
      const cities = mapCitiesResponse(cityRes);
      cityLabel = cities.find((c) => c.id === cyid)?.label || cyid;
    }

    return { country: countryLabel, state: stateLabel, city: cityLabel };
  } catch {
    return { country: cid, state: sid || "—", city: cyid || "—" };
  }
}

function rfqSummaryFromPayload(r) {
  const p = r?.payload && typeof r.payload === "object" ? r.payload : {};
  const kind = r?.rfqKind != null ? String(r.rfqKind).trim().toLowerCase() : "generic";

  const weight =
    p.totalWeightValue != null || p.totalWeightUnit != null
      ? `${p.totalWeightValue || "—"} ${p.totalWeightUnit || ""}`.trim()
      : p.totalWeightKg
        ? `${p.totalWeightKg} kg`
        : "—";

  let volume =
    p.totalVolumeValue != null || p.totalVolumeUnit != null
      ? `${p.totalVolumeValue || "—"} ${p.totalVolumeUnit || ""}`.trim()
      : p.totalVolumeCbm
        ? `${p.totalVolumeCbm} CBM`
        : "—";

  const labels = {
    origin: "Origin",
    destination: "Destination",
    cargoType: "Cargo Type",
    container: "Container",
    weight: "Weight",
    volume: "Volume",
    incoterm: "Incoterms",
    expectedDate: "Expected Date",
    requirements: "Preferred payment terms",
  };

  let origin = r?.resolvedLocations?.origin || "—";
  let destination = r?.resolvedLocations?.destination || "—";
  let cargoType = p.commodityType || p.cargoDescription || "—";
  let container = p.modeDetail || "—";
  let incoterm = p.incoterm || "—";
  let expectedDate = p.deliveryDate || p.pickupDate || "—";
  let requirements = p.preferredPaymentTerms || p.creditTerms || p.paymentMode || "—";

  if (kind === "business-relocation") {
    labels.origin = "Current location";
    labels.destination = "Destination";
    labels.cargoType = "Service";
    labels.container = "Type of Relocation";
    labels.incoterm = "Logistics mode";
    labels.expectedDate = "Preferred move date";
    labels.requirements = "Notes";
    labels.volume = "Budget Range (USD)";

    origin = r?.resolvedLocations?.origin || "—";
    destination = r?.resolvedLocations?.destination || "—";
    cargoType = "Business relocation";
    container = p.relocationScope || "—";
    incoterm = p.logisticsMode || "—";
    expectedDate = p.preferredMoveDate || "—";
    requirements = p.specialNotes || "—";
    volume = p.budgetRange || "—";
  } else if (kind === "exhibition-logistics") {
    labels.destination = "Expo venue";
    labels.cargoType = "Materials";
    labels.container = "Packaging / Booth";
    labels.incoterm = "Logistics mode";
    labels.expectedDate = "Expo start date";
    labels.requirements = "Special handling";
    labels.volume = "Urgency level";

    origin = r?.resolvedLocations?.origin || "—";
    destination = r?.resolvedLocations?.destination || "—";
    cargoType = p.materialsDescription || "Exhibition materials";
    container = p.packagingType || p.boothNumber || "—";
    incoterm = p.logisticsMode || "—";
    expectedDate = p.exhibitionStartDate || p.setupDate || "—";
    requirements = p.specialHandling || "—";
    volume = p.urgencyLevel || "—";
  }

  return { labels, origin, destination, cargoType, container, weight, volume, incoterm, expectedDate, requirements };
}

function readMaybeJson(v) {
  if (!v) return null;
  if (typeof v === "object") return v;
  if (typeof v === "string") {
    try {
      const j = JSON.parse(v);
      return j && typeof j === "object" ? j : null;
    } catch {
      return null;
    }
  }
  return null;
}

export default function LogisticsQuoteDetailsPage() {
  const { submissionId, quoteId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rfq, setRfq] = useState(null);
  const [resolvedLocations, setResolvedLocations] = useState(null);
  const [quoteWrapper, setQuoteWrapper] = useState(null);
  const [providerInfo, setProviderInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sid = submissionId != null ? parseInt(String(submissionId), 10) : NaN;
        const qid = quoteId != null ? parseInt(String(quoteId), 10) : NaN;
        if (!Number.isFinite(sid) || sid <= 0 || !Number.isFinite(qid) || qid <= 0) throw new Error("Invalid id");

        const [rfqRes, quoteRes] = await Promise.all([
          CommonService.get(`/company/logistics-providers/rfq/submission/${sid}`),
          CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_QUOTE_DETAILS(sid, qid)),
        ]);

        const rfqBody = rfqRes?.data ?? rfqRes ?? {};
        const rfqData = rfqBody?.data ?? rfqBody;

        const quoteBody = quoteRes?.data ?? quoteRes ?? {};
        const quoteData = quoteBody?.data ?? quoteBody;
        const quote = quoteData?.quote || null;

        if (cancelled) return;
        setRfq(rfqData);
        setQuoteWrapper(quote);

        const providerCompanyId = quote?.providerCompanyId;
        if (providerCompanyId) {
          try {
            const provRes = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.PUBLIC_PROFILE(providerCompanyId));
            const pb = provRes?.data ?? provRes ?? {};
            const pd = pb?.data ?? pb;
            const contact = readMaybeJson(pd?.contact_details) || readMaybeJson(pd?.contactDetails) || {};
            setProviderInfo({
              providerName: pd?.company_name || pd?.companyName || pd?.companyName || quote?.providerCompanyName || "—",
              contactNumber:
                contact?.primaryPhone ||
                contact?.primary_phone ||
                contact?.contact_number ||
                contact?.contactNumber ||
                contact?.phone ||
                pd?.user_phone ||
                "—",
              email:
                contact?.primaryEmail ||
                contact?.primary_email ||
                contact?.contact_email ||
                contact?.contactEmail ||
                contact?.email ||
                pd?.user_email ||
                "—",
            });
          } catch {
            setProviderInfo({
              providerName: quote?.providerCompanyName || "—",
              contactNumber: "—",
              email: "—",
            });
          }
        } else {
          setProviderInfo({
            providerName: quote?.providerCompanyName || "—",
            contactNumber: "—",
            email: "—",
          });
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || "Could not load quote details.");
        if (!cancelled) {
          setRfq(null);
          setQuoteWrapper(null);
          setProviderInfo(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [submissionId, quoteId]);

  useEffect(() => {
    let cancelled = false;
    if (!rfq?.payload) return undefined;
    const p = rfq.payload;
    const kind = rfq?.rfqKind != null ? String(rfq.rfqKind).trim().toLowerCase() : "generic";
    const pick = () => {
      if (kind === "business-relocation") {
        return {
          origin: { c: p.currentCountry, s: p.currentState, cy: p.currentCity },
          destination: { c: p.destCountry, s: p.destState, cy: p.destCity },
        };
      }
      if (kind === "exhibition-logistics") {
        return {
          origin: { c: p.orgCountry, s: p.orgState, cy: p.orgCity },
          destination: { c: p.venueCountry, s: p.venueState, cy: p.venueCity },
        };
      }
      return {
        origin: { c: p.originCountry, s: p.originState, cy: p.originCity },
        destination: { c: p.destCountry, s: p.destState, cy: p.destCity },
      };
    };
    (async () => {
      const sel = pick();
      const [o, d] = await Promise.all([
        resolveLocationLabels(sel.origin.c, sel.origin.s, sel.origin.cy),
        resolveLocationLabels(sel.destination.c, sel.destination.s, sel.destination.cy),
      ]);
      if (cancelled) return;
      const join = (x) => [x.city, x.state, x.country].filter(Boolean).join(", ") || "—";
      setResolvedLocations({ origin: join(o), destination: join(d) });
    })();
    return () => {
      cancelled = true;
    };
  }, [rfq?.id]);

  const quote = quoteWrapper?.quote && typeof quoteWrapper.quote === "object" ? quoteWrapper.quote : {};
  const currency = quote.currency || quoteWrapper?.currency || "USD";
  const totalAmount = quoteWrapper?.totalAmount != null ? Number(quoteWrapper.totalAmount) : toNum(quote.totalAmount);
  const summary = rfqSummaryFromPayload({ ...rfq, resolvedLocations });
  const quoteStatus = quoteWrapper?.status != null ? String(quoteWrapper.status).trim().toLowerCase() : "";
  const isSubmittedView = quoteStatus === "submitted" || quoteStatus === "accepted";

  const checklist = useMemo(() => {
    return [
      "All charges filled",
      "Loading/Unloading inclusion clearly marked",
      "Warehouse charges include any free days",
      "Free time & detention specified",
      "Exclusions clearly mentioned",
    ];
  }, []);

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!rfq || !quoteWrapper) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">Quote not found.</div>
        <Link to={`/logistics-rfqs-sent/${submissionId}/quotes`} className="btn btn-outline-secondary">
          Back
        </Link>
      </div>
    );
  }

  return (
    <div className="lq-page">
      <div className="container-fluid py-4">
        <div className="lq-topbar mb-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <button type="button" className="btn btn-link p-0 fw-bold text-decoration-none" onClick={() => navigate(-1)}>
                ←
              </button>
              <h2 className="mb-0">{isSubmittedView ? "View Quote" : "Quote Details"}</h2>
            </div>
            <div className="lq-breadcrumb-mini">
              Dashboard &nbsp;›&nbsp; {isSubmittedView ? "View Quote" : "Quote Details"}
            </div>
          </div>

          <div className="lq-chip-row">
            <Link
              to={`/logistics-providers/rfq/view?id=${rfq.id}&from=sent`}
              className="lq-chip text-decoration-none"
            >
              View Full RFQ
            </Link>
            <span className="lq-chip">RFQ {rfq?.rfqReferenceNumber || "—"}</span>
          </div>
        </div>

        <div className="lq-main-grid">
          <div>
            {/* RFQ Summary */}
            <div className="lq-card">
              <div className="lq-card-head lq-card-head--summary">
                <LogisticsQuoteSectionIcon name="rfqSummary" /> RFQ Summary
              </div>
              <div className="lq-card-body">
                <div className="lq-kv-grid">
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.origin || "Origin"}</div>
                    <div className="lq-v">{summary.origin}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.destination || "Destination"}</div>
                    <div className="lq-v">{summary.destination}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.cargoType || "Cargo Type"}</div>
                    <div className="lq-v">{summary.cargoType}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.container || "Container"}</div>
                    <div className="lq-v">{summary.container}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.weight || "Weight"}</div>
                    <div className="lq-v">{summary.weight}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.volume || "Volume"}</div>
                    <div className="lq-v">{summary.volume}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.incoterm || "Incoterms"}</div>
                    <div className="lq-v">{summary.incoterm}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.expectedDate || "Expected Date"}</div>
                    <div className="lq-v">{formatExpoDate(summary.expectedDate)}</div>
                  </div>
                </div>
                <div className="lq-muted-box">
                  <div className="fw-bold mb-1">{summary.labels?.requirements || "Requirements"}:</div>
                  <div className="lq-muted-box__content">{String(summary.requirements)}</div>
                </div>
              </div>
            </div>

            {/* Provider Info */}
            <div className="lq-card">
              <div className="lq-card-head lq-card-head--provider">
                <LogisticsQuoteSectionIcon name="provider" /> Provider Information
              </div>
              <div className="lq-card-body">
                <div className="lq-field-row">
                  <div>
                    <label className="lq-field-label">Provider Name</label>
                    <input className="lq-field-control" value={providerInfo?.providerName || "—"} readOnly />
                  </div>
                  <div>
                    <label className="lq-field-label">Contact number</label>
                    <input className="lq-field-control" value={providerInfo?.contactNumber || "—"} readOnly />
                  </div>
                  <div>
                    <label className="lq-field-label">Email</label>
                    <input className="lq-field-control" value={providerInfo?.email || "—"} readOnly />
                  </div>
                </div>
              </div>
            </div>

            {/* Quote Information (read-only) */}
            <div className="lq-card lq-card--quote">
              <div className="lq-card-head lq-card-head--quote">
                <LogisticsQuoteSectionIcon name="quote" /> Quote Information
              </div>
              <div className="lq-card-body">
                <div className="lq-section-title">
                  <LogisticsQuoteSectionIcon name="basicInfo" />
                  Basic Information
                </div>
                <div className="lq-field-row-4">
                  <div>
                    <label className="lq-field-label">Currency</label>
                    <input className="lq-field-control" value={currency} readOnly />
                  </div>
                  <div>
                    <label className="lq-field-label">Validity (Days)</label>
                    <input className="lq-field-control" value={quote.validityDays ?? ""} readOnly />
                  </div>
                  <div>
                    <label className="lq-field-label">Transit Time (Days)</label>
                    <input className="lq-field-control" value={quote.transitDays ?? ""} readOnly />
                  </div>
                  <div>
                    <label className="lq-field-label">Quote Expiry Date</label>
                    <div className="lq-input-icon">
                      <input type="date" className="lq-field-control lq-field-control--icon-right" value={quote.quoteExpiryDate || ""} readOnly />
                      <span className="lq-input-icon-right" aria-hidden>
                        <LogisticsQuoteSectionIcon name="calendar" className="lq-input-calendar-icon" />
                      </span>
                    </div>
                  </div>
                </div>

                <div className="lq-divider" />
                <div className="lq-section-title">
                  <LogisticsQuoteSectionIcon name="vesselTimeline" />
                  Vessel &amp; Timeline
                </div>
                <div className="lq-field-row-4">
                  <div>
                    <label className="lq-field-label">Vessel Name</label>
                    <input className="lq-field-control" value={quote.vesselName || ""} readOnly />
                  </div>
                  <div>
                    <label className="lq-field-label">Voyage Number</label>
                    <input className="lq-field-control" value={quote.voyageNumber || ""} readOnly />
                  </div>
                  <div>
                    <label className="lq-field-label">Est. Departure</label>
                    <div className="lq-input-icon">
                      <input type="date" className="lq-field-control lq-field-control--icon-right" value={quote.estDeparture || ""} readOnly />
                      <span className="lq-input-icon-right" aria-hidden>
                        <LogisticsQuoteSectionIcon name="calendar" className="lq-input-calendar-icon" />
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="lq-field-label">Est. Arrival</label>
                    <div className="lq-input-icon">
                      <input type="date" className="lq-field-control lq-field-control--icon-right" value={quote.estArrival || ""} readOnly />
                      <span className="lq-input-icon-right" aria-hidden>
                        <LogisticsQuoteSectionIcon name="calendar" className="lq-input-calendar-icon" />
                      </span>
                    </div>
                  </div>
                </div>

                <div className="lq-divider" />
                <div className="lq-section-title">
                  <LogisticsQuoteSectionIcon name="coreCharges" />
                  Core Charges (Included)
                </div>
                <div className="lq-field-row-4">
                  {[
                    ["Ocean Freight", "oceanFreight", "3200"],
                    ["BAF / Fuel Surcharge", "fuelSurcharge", "200"],
                    ["Port Handling", "portHandling", "450"],
                    ["Documentation", "documentation", "150"],
                  ].map(([label, key, ph]) => (
                    <div key={key}>
                      <label className="lq-field-label">{label}</label>
                      <div className="lq-money">
                        <span className="lq-money-prefix">{currency}</span>
                        <input className="lq-field-control lq-field-control--money" value={quote[key] ?? ""} placeholder={ph} readOnly />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="lq-field-row lq-field-row--spaced-top">
                  <div>
                    <label className="lq-field-label">Container Deposit (Refundable)</label>
                    <div className="lq-money lq-money--wide">
                      <span className="lq-money-prefix">{currency}</span>
                      <input className="lq-field-control lq-field-control--money" value={quote.containerDeposit ?? ""} placeholder="500" readOnly />
                    </div>
                  </div>
                </div>

                {/* The rest of sections already match existing design; render read-only using same values */}
                <div className="lq-divider" />
                <div className="lq-section-title">
                  <LogisticsQuoteSectionIcon name="loadingUnloading" />
                  Loading &amp; Unloading Charges
                </div>

                <div className="lq-lu-card">
                  <div className="lq-lu-row">
                    <div>
                      <label className="lq-field-label">Loading Charges</label>
                      <div className="lq-money lq-money--wide">
                        <span className="lq-money-prefix">{currency}</span>
                        <input className="lq-field-control lq-field-control--money" value={quote.loadingCharge ?? ""} readOnly />
                      </div>
                    </div>
                    <div>
                      <label className="lq-field-label">Inclusion Status</label>
                      <div className="lq-inc-box">
                        <label className="lq-switch">
                          <input type="checkbox" checked={!!quote.loadingIncluded} readOnly disabled />
                          <span className="lq-slider" aria-hidden />
                        </label>
                        {quote.loadingIncluded ? (
                          <div className="lq-inc-text lq-inc-text--on">
                            <span className="lq-inc-check" aria-hidden>
                              ✓
                            </span>
                            Included in Quote
                          </div>
                        ) : (
                          <div className="lq-inc-text lq-inc-text--off">
                            <span className="lq-inc-plus" aria-hidden>
                              ⊕
                            </span>
                            Additional Charges
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="lq-field-label">Location</label>
                      <input className="lq-field-control" value={quote.loadingLocation || ""} readOnly />
                    </div>
                  </div>
                </div>

                <div className="lq-lu-card">
                  <div className="lq-lu-row">
                    <div>
                      <label className="lq-field-label">Unloading Charges</label>
                      <div className="lq-money lq-money--wide">
                        <span className="lq-money-prefix">{currency}</span>
                        <input className="lq-field-control lq-field-control--money" value={quote.unloadingCharge ?? ""} readOnly />
                      </div>
                    </div>
                    <div>
                      <label className="lq-field-label">Inclusion Status</label>
                      <div className="lq-inc-box">
                        <label className="lq-switch">
                          <input type="checkbox" checked={!!quote.unloadingIncluded} readOnly disabled />
                          <span className="lq-slider" aria-hidden />
                        </label>
                        {quote.unloadingIncluded ? (
                          <div className="lq-inc-text lq-inc-text--on">
                            <span className="lq-inc-check" aria-hidden>
                              ✓
                            </span>
                            Included in Quote
                          </div>
                        ) : (
                          <div className="lq-inc-text lq-inc-text--off">
                            <span className="lq-inc-plus" aria-hidden>
                              ⊕
                            </span>
                            Additional Charges
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="lq-field-label">Location</label>
                      <input className="lq-field-control" value={quote.unloadingLocation || ""} readOnly />
                    </div>
                  </div>
                </div>

                <div className="lq-divider" />
                <div className="lq-section-title">
                  <LogisticsQuoteSectionIcon name="additionalServices" />
                  Additional Services (Optional)
                </div>

                <div className="lq-subcard lq-subcard--blue">
                  <div className="lq-subcard-head">
                    <LogisticsQuoteSectionIcon name="cargoInsurance" /> Cargo Insurance
                    <span className="ms-auto" />
                    <label className="lq-switch lq-switch--sm">
                      <input type="checkbox" checked={!!quote.cargoInsuranceOn} readOnly disabled />
                      <span className="lq-slider" aria-hidden />
                    </label>
                  </div>
                  {quote.cargoInsuranceOn ? (
                    <div className="lq-subcard-body">
                      <div className="lq-field-row">
                        <div>
                          <label className="lq-field-label">Insurance Amount</label>
                          <div className="lq-money lq-money--wide">
                            <span className="lq-money-prefix">{currency}</span>
                            <input className="lq-field-control lq-field-control--money" value={quote.insuranceAmount ?? ""} readOnly />
                          </div>
                        </div>
                        <div>
                          <label className="lq-field-label">Coverage %</label>
                          <input className="lq-field-control" value={quote.coveragePercent ?? ""} readOnly />
                        </div>
                        <div>
                          <label className="lq-field-label">Coverage Type</label>
                          <input className="lq-field-control" value={quote.coverageType ?? ""} readOnly />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="lq-subcard lq-subcard--purple">
                  <div className="lq-subcard-head">
                    <LogisticsQuoteSectionIcon name="customsClearance" /> Customs Clearance
                    <span className="ms-auto" />
                    <label className="lq-switch lq-switch--sm">
                      <input type="checkbox" checked={!!quote.customsOn} readOnly disabled />
                      <span className="lq-slider" aria-hidden />
                    </label>
                  </div>
                  {quote.customsOn ? (
                    <div className="lq-subcard-body">
                      <div className="lq-field-row">
                        <div>
                          <label className="lq-field-label">Customs Clearance Charges</label>
                          <div className="lq-money lq-money--wide">
                            <span className="lq-money-prefix">{currency}</span>
                            <input className="lq-field-control lq-field-control--money" value={quote.customsClearanceCharge ?? ""} readOnly />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="lq-subcard lq-subcard--green">
                  <div className="lq-subcard-head">
                    <LogisticsQuoteSectionIcon name="warehousing" /> Warehousing / Storage
                    <span className="ms-auto" />
                    <label className="lq-switch lq-switch--sm">
                      <input type="checkbox" checked={!!quote.warehousingOn} readOnly disabled />
                      <span className="lq-slider" aria-hidden />
                    </label>
                  </div>
                  {quote.warehousingOn ? (
                    <div className="lq-subcard-body">
                      <div className="lq-field-row">
                        <div>
                          <label className="lq-field-label">Free Days Included</label>
                          <input className="lq-field-control" value={quote.freeDaysIncluded ?? ""} readOnly />
                        </div>
                        <div>
                          <label className="lq-field-label">Base Storage Charge</label>
                          <div className="lq-money lq-money--wide">
                            <span className="lq-money-prefix">{currency}</span>
                            <input className="lq-field-control lq-field-control--money" value={quote.baseStorageCharge ?? ""} readOnly />
                          </div>
                        </div>
                        <div>
                          <label className="lq-field-label">Per Day Charge (After Free Days)</label>
                          <div className="lq-money lq-money--wide">
                            <span className="lq-money-prefix">{currency}</span>
                            <input className="lq-field-control lq-field-control--money" value={quote.perDayChargeAfterFree ?? ""} readOnly />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="lq-subcard lq-subcard--yellow">
                  <div className="lq-subcard-head">
                    <LogisticsQuoteSectionIcon name="pickup" /> Pickup Service
                    <span className="ms-auto" />
                    <label className="lq-switch lq-switch--sm">
                      <input type="checkbox" checked={!!quote.pickupOn} readOnly disabled />
                      <span className="lq-slider" aria-hidden />
                    </label>
                  </div>
                  {quote.pickupOn ? (
                    <div className="lq-subcard-body">
                      <div className="lq-field-row">
                        <div>
                          <label className="lq-field-label">Pickup Location</label>
                          <input className="lq-field-control" value={quote.pickupLocation ?? ""} readOnly />
                        </div>
                        <div>
                          <label className="lq-field-label">Pickup Charges</label>
                          <div className="lq-money lq-money--wide">
                            <span className="lq-money-prefix">{currency}</span>
                            <input className="lq-field-control lq-field-control--money" value={quote.pickupCharge ?? ""} readOnly />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="lq-subcard lq-subcard--teal">
                  <div className="lq-subcard-head">
                    <LogisticsQuoteSectionIcon name="delivery" /> Delivery Service
                    <span className="ms-auto" />
                    <label className="lq-switch lq-switch--sm">
                      <input type="checkbox" checked={!!quote.deliveryOn} readOnly disabled />
                      <span className="lq-slider" aria-hidden />
                    </label>
                  </div>
                  {quote.deliveryOn ? (
                    <div className="lq-subcard-body">
                      <div className="lq-field-row">
                        <div>
                          <label className="lq-field-label">Delivery Location</label>
                          <input className="lq-field-control" value={quote.deliveryLocation ?? ""} readOnly />
                        </div>
                        <div>
                          <label className="lq-field-label">Delivery Charges</label>
                          <div className="lq-money lq-money--wide">
                            <span className="lq-money-prefix">{currency}</span>
                            <input className="lq-field-control lq-field-control--money" value={quote.deliveryCharge ?? ""} readOnly />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="lq-divider" />
                <div className="lq-section-title">
                  <LogisticsQuoteSectionIcon name="freeTimeDetention" />
                  Free Time &amp; Detention Charges
                </div>
                <div className="lq-field-row-4">
                  <div>
                    <label className="lq-field-label">Free Days at Origin</label>
                    <input className="lq-field-control" value={quote.freeDaysOrigin ?? ""} readOnly />
                  </div>
                  <div>
                    <label className="lq-field-label">Free Days at Destination</label>
                    <input className="lq-field-control" value={quote.freeDaysDestination ?? ""} readOnly />
                  </div>
                  <div>
                    <label className="lq-field-label">Detention per Day</label>
                    <div className="lq-money">
                      <span className="lq-money-prefix">{currency}</span>
                      <input className="lq-field-control lq-field-control--money" value={quote.detentionPerDay ?? ""} readOnly />
                    </div>
                  </div>
                  <div>
                    <label className="lq-field-label">Demurrage per Day</label>
                    <div className="lq-money">
                      <span className="lq-money-prefix">{currency}</span>
                      <input className="lq-field-control lq-field-control--money" value={quote.demurragePerDay ?? ""} readOnly />
                    </div>
                  </div>
                </div>

                <div className="lq-divider" />
                <div className="lq-section-title">
                  <LogisticsQuoteSectionIcon name="paymentTerms" />
                  Payment Terms &amp; Additional Information
                </div>
                <div className="lq-two-col">
                  <div>
                    <label className="lq-field-label">Payment Terms</label>
                    <input className="lq-field-control" value={quote.paymentTerms ?? ""} readOnly />
                  </div>
                  <div>
                    <label className="lq-field-label">Exclusions</label>
                    <input className="lq-field-control" value={quote.exclusions ?? ""} readOnly />
                  </div>
                </div>
                <div className="lq-two-col" style={{ marginTop: 10 }}>
                  <div>
                    <label className="lq-field-label">Special Terms &amp; Conditions</label>
                    <input className="lq-field-control" value={quote.specialTerms ?? ""} readOnly />
                  </div>
                  <div>
                    <label className="lq-field-label">Additional Notes</label>
                    <input className="lq-field-control" value={quote.additionalNotes ?? ""} readOnly />
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <label className="lq-field-label">General Terms &amp; Conditions</label>
                  <input className="lq-field-control" value={quote.generalTerms ?? ""} readOnly />
                </div>
              </div>
            </div>
          </div>

          {/* Right Quote Summary panel (no actions) */}
          <div className="lq-right-sticky">
            <div className="lq-quote-summary">
              <div className="lq-quote-summary-head">
                <LogisticsQuoteSectionIcon name="quoteSummary" />
                Quote Summary
              </div>
              <div className="p-3">
                <div className="lq-total-bar" style={{ marginTop: 0 }}>
                  <div className="lq-total-left">
                    <div className="lq-total-label">Total Quote Amount</div>
                    <div className="lq-total-value">
                      {currency} {Math.max(0, Number.isFinite(totalAmount) ? totalAmount : 0).toFixed(0)}
                    </div>
                  </div>
                  <div className="lq-total-right">
                    <div className="opacity-90 small">Transit</div>
                    <div>{toNum(quote.transitDays) || 0} days</div>
                  </div>
                </div>

                <div className="lq-checklist">
                  <div className="lq-checklist-title">
                    <LogisticsQuoteSectionIcon name="checklist" />
                    Checklist
                  </div>
                  {checklist.map((t) => (
                    <div className="lq-check-item" key={t}>
                      <span className="lq-check-dot" aria-hidden />
                      <span>{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3">
          <Link to={`/logistics-rfqs-sent/${submissionId}/quotes`} className="btn btn-outline-secondary">
            Back to Compare Quotes
          </Link>
        </div>
      </div>
    </div>
  );
}

